import { useState, useEffect, useMemo, useRef } from 'react';
import Card from '../components/common/Card.jsx';
import Table, { UtilBar } from '../components/common/Table.jsx';
import Tabs from '../components/common/Tabs.jsx';
import LineChart from '../components/charts/LineChart.jsx';
import VerticalBarChart from '../components/charts/VerticalBarChart.jsx';
import HorizontalBarChart from '../components/charts/HorizontalBarChart.jsx';
import DonutChart from '../components/charts/DonutChart.jsx';

import KpiRow from '../components/optimization/KpiRow.jsx';
import TopActionsCard from '../components/optimization/TopActionsCard.jsx';
import AIModal from '../components/optimization/AIModal.jsx';
import { BadgeSkeleton } from '../components/optimization/Skeletons.jsx';
import { CostCell, RecommendedCostCell, SavingCell, RecCell } from '../components/optimization/RecCells.jsx';
import {
  parseCost, parseSaving, formatSavingStr,
  buildBaseCostKpis, exportCSV, buildGroupedCostDonut, buildExportFilename, ADD_NEW_ACCOUNT,
  setScopedItem, STORAGE_KEYS,
} from '../components/common/Helper';
import MultiSelect from '../components/common/MultiSelect.jsx';

import OptimizationService from '../services/OptimizationService';
import { useAuth } from '../state/AuthContext';
import useTagList from '../hooks/useTagList';

const tabs = [
  { label: 'Combined Recommendations' },
  { label: 'CloudsAnalytics Recommendations' },
  { label: 'AWS Recommendations' },
];

// ── Row Mappers ────────────────────────────────────────────
const resolveEC2Rec = (recType) => {
  const displayVal = recType ?? '';
  const t = displayVal.toUpperCase().trim();
  
  let recPillClass = 'rec-rs';
  if (t === 'OPTIMAL' || t === 'OPTIMIZED' || t === 'NONE' || t === 'NA' || t === '') {
    recPillClass = 'rec-ok';
  } else if (t === 'TERMINATE' || t === 'DELETE' || t === 'IDLE' || t === 'RELEASE' || t === 'UNATTACHED') {
    recPillClass = 'rec-id';
  } else if (t === 'STOP' || t === 'STOPPED' || t === 'SCHEDULE') {
    recPillClass = 'rec-warn';
  }

  return {
    recType: displayVal,
    recPillClass
  };
};

const mapCombinedRows = (data) =>
  data.map((item) => {
    const current = parseCost(item.onDemandMonthlyPriceValue);
    const recommended = parseCost(item.recommendedOnDemandMonthlyPriceValue ?? item.customRecommendedOnDemandMonthlyPriceValue);
    const saving = item.savingCost != null ? parseCost(item.savingCost) : (current - recommended);
    const fallbackRec = item.recommendedBy === 'Cloudsanalytics' ? 'RIGHTSIZE' : 'OPTIMAL';
    const rawRecType = item.recommendationType ?? fallbackRec;
    const { recType, recPillClass } = resolveEC2Rec(rawRecType);

    return {
      id: item.instanceId,
      name: item.instanceName,
      status: item.stateName === 'running' ? 'Running' : 'Stopped',
      type: item.instanceType,
      region: item.region,
      cpu: item.averageCPUValue ?? null,
      peakCpu: item.peakCPUValue ?? null,
      p99Cpu: item.p99CPUValue ?? null,
      p95Cpu: item.p95CPUValue ?? null,
      mem: item.memory
        ? (item.recommendedMemory && item.recommendedMemory !== 'NA' && item.recommendedMemory !== item.memory
            ? `${item.memory} → ${item.recommendedMemory}`
            : item.memory)
        : '—',
      cost: `$${current.toFixed(2)}`,
      costValue: current,
      recommendedCost: recommended > 0 ? `$${recommended.toFixed(2)}` : '—',
      recommendedCostValue: recommended,
      recommendationRemark: item.customRecomendationRemark ?? 'No recommendation',
      recType,
      recPillClass,
      recLabel: item.recommendedInstanceType && item.recommendedInstanceType !== 'NA'
        ? `→ ${item.recommendedInstanceType}` : '',
      saving: formatSavingStr(saving),
      actionLabel: 'Assist with AI',
      actionClass: 'mini-btn ai-assist',
      checked: false,
    };
  });

const mapCustomRows = (data) =>
  data.map((item) => {
    const current = parseCost(item.currentOnDemandMonthlyPriceValue);
    const recommended = parseCost(item.customRecommendedOnDemandMonthlyPriceValue);
    const saving = item.savingCost != null ? parseCost(item.savingCost) : (current - recommended);
    const remark = item.customRecomendationRemark ?? '';
    const remarkUpper = remark.toUpperCase().trim();
    const isRightSize = remarkUpper !== 'NA' && remarkUpper !== 'EC2 IS STOPPED - NO CHARGES' && remarkUpper !== '';
    const fallbackRec = isRightSize ? 'RIGHTSIZE' : 'OPTIMAL';
    const rawRecType = item.recommendationType ?? fallbackRec;
    const { recType, recPillClass } = resolveEC2Rec(rawRecType);

    return {
      id: item.instanceId,
      name: item.instanceName,
      status: item.stateName === 'running' ? 'Running' : 'Stopped',
      type: item.instanceType,
      region: item.region,
      cpu: item.averageCPUValue ?? null,
      peakCpu: item.peakCPUValue ?? null,
      p99Cpu: item.p99CPUValue ?? null,
      p95Cpu: item.p95CPUValue ?? null,
      mem: item.currentMemory
        ? (item.customRecommendedMemory && item.customRecommendedMemory !== 'NA' && item.customRecommendedMemory !== item.currentMemory
            ? `${item.currentMemory} → ${item.customRecommendedMemory}`
            : item.currentMemory)
        : '—',
      cost: `$${current.toFixed(2)}`,
      costValue: current,
      recommendedCost: recommended > 0 ? `$${recommended.toFixed(2)}` : '—',
      recommendedCostValue: recommended,
      recommendationRemark: remark !== '' && remark !== 'NA' ? remark : 'Optimal',
      recType,
      recPillClass,
      recLabel: item.customRecommendedInstance && item.customRecommendedInstance !== 'NA'
        ? `→ ${item.customRecommendedInstance}` : '',
      saving: formatSavingStr(saving),
      tags: item.Tags ?? item.tags ?? [],
      actionLabel: 'Assist with AI',
      actionClass: 'mini-btn ai-assist',
      checked: false,
      instanceRunningHours: item.instanceRunningHours ?? 0,
    };
  });

const mapAWSRows = (data) => {
  const recTypeMap = { OVER_PROVISIONED: 'RIGHTSIZE', UNDER_PROVISIONED: 'SCALE-UP', OPTIMIZED: 'OPTIMAL' };
  return data.map((item) => {
    const current = parseCost(item.currentOnDemandMonthlyPriceValue);
    const recommended = parseCost(item.recommendedOnDemandMonthlyPriceValue);
    const saving = current - recommended;
    const finding = item.finding ?? 'OPTIMIZED';
    const reasonStr = item.findingReasonCodes?.length ? ` (${item.findingReasonCodes.join(', ')})` : '';
    const fallbackRec = recTypeMap[finding] ?? 'OPTIMAL';
    const rawRecType = item.recommendationType ?? fallbackRec;
    const { recType, recPillClass } = resolveEC2Rec(rawRecType);

    return {
      id: item.instanceId,
      name: item.instanceName,
      status: item.stateName === 'running' ? 'Running' : 'Stopped',
      type: item.currentInstanceType ?? item.instanceType,
      region: item.region,
      cpu: item.averageCPUValue ?? null,
      peakCpu: item.peakCPUValue ?? null,
      p99Cpu: item.p99CPUValue ?? null,
      p95Cpu: item.p95CPUValue ?? null,
      mem: item.currentMemory
        ? (item.recommendedMemory && item.recommendedMemory !== 'NA' && item.recommendedMemory !== item.currentMemory
            ? `${item.currentMemory} → ${item.recommendedMemory}`
            : item.currentMemory)
        : '—',
      cost: `$${current.toFixed(2)}`,
      costValue: current,
      recommendedCost: recommended > 0 ? `$${recommended.toFixed(2)}` : '—',
      recommendedCostValue: recommended,
      recommendationRemark:
        finding === 'OVER_PROVISIONED' ? `Consider downsizing${reasonStr}`
        : finding === 'UNDER_PROVISIONED' ? `Scale up recommended${reasonStr}`
        : 'Instance is optimized',
      recType,
      recPillClass,
      recLabel: item.RecommendedinstanceType ? `→ ${item.RecommendedinstanceType}` : '',
      saving: formatSavingStr(saving),
      actionLabel: 'Assist with AI',
      actionClass: 'mini-btn ai-assist',
      checked: false,
    };
  });
};

// ── KPI Builder ────────────────────────────────────────────
const buildKpiData = (totalPriceValue, totalInstances, rows = []) => {
  const current = parseCost(totalPriceValue?.currentCost);
  const recommended = parseCost(totalPriceValue?.recommendedCost);
  const saving = parseCost(totalPriceValue?.savingCost);
  const runningCount = rows.filter((r) => r.stateName === 'running').length;
  const stoppedCount = rows.filter((r) => r.stateName !== 'running').length;
  return buildBaseCostKpis({
    countTitle: 'Total Instances',
    countValue: totalInstances,
    countSubtitle: `${runningCount} running · ${stoppedCount} stopped`,
    current, saving, recommended,
  });
};

// ── Chart Builders ─────────────────────────────────────────
const buildByTypeChartData = (rows) => {
  if (!rows.length) return null;
  const typeMap = {};
  rows.forEach((row) => {
    const cost = parseFloat((row.cost ?? '$0').replace('$', '')) || 0;
    typeMap[row.type] = (typeMap[row.type] || 0) + cost;
  });
  const sorted = Object.entries(typeMap).sort((a, b) => b[1] - a[1]).slice(0, 6);
  if (!sorted.reduce((s, [, v]) => s + v, 0)) return null;
  return {
    labels: sorted.map(([t]) => t),
    datasets: [{ data: sorted.map(([, c]) => parseFloat(c.toFixed(2))), backgroundColor: '#f57c00', borderRadius: 4, barPercentage: 0.6 }],
  };
};

const buildTrendChartData = (rows) => {
  const running = rows
    .filter((r) => r.status === 'Running' && r.cpu !== null && r.cpu > 0)
    .map((r) => ({ name: r.name, cpu: r.cpu ?? 0, cost: parseFloat((r.cost ?? '$0').replace('$', '')) || 0 }))
    .filter((r) => r.cost > 0)
    .sort((a, b) => b.cost - a.cost)
    .slice(0, 20);
  if (!running.length) return null;
  return {
    labels: running.map((r) => r.name),
    datasets: [
      { label: 'Avg CPU %', data: running.map((r) => r.cpu), backgroundColor: 'rgba(33,150,243,0.7)', borderColor: '#2196f3', borderWidth: 1, borderRadius: 4, yAxisID: 'y1' },
      { label: 'Monthly cost ($)', data: running.map((r) => r.cost), backgroundColor: 'rgba(245,124,0,0.7)', borderColor: '#f57c00', borderWidth: 1, borderRadius: 4, yAxisID: 'y2' },
    ],
  };
};

const buildRightsizeChartData = (rows) => {
  if (!rows.length) return null;
  const buckets = { '< 10%': 0, '10–30%': 0, '30–50%': 0, '50–70%': 0, '70–90%': 0, '> 90%': 0 };
  rows.forEach(({ cpu }) => {
    if (cpu === null) return;
    if (cpu < 10) buckets['< 10%']++;
    else if (cpu < 30) buckets['10–30%']++;
    else if (cpu < 50) buckets['30–50%']++;
    else if (cpu < 70) buckets['50–70%']++;
    else if (cpu < 90) buckets['70–90%']++;
    else buckets['> 90%']++;
  });
  if (!Object.values(buckets).reduce((s, v) => s + v, 0)) return null;
  return {
    labels: Object.keys(buckets),
    datasets: [{ data: Object.values(buckets), backgroundColor: ['#ef4444', '#f57c00', '#fbc02d', '#2ea66f', '#1e88e5', '#ef4444'], borderRadius: 4, barPercentage: 0.7 }],
  };
};

const buildInsightActions = (rows) => {
  const seen = new Set();
  const withSaving = rows
    .filter((r) => {
      const key = `${r.id}|${r.recType}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return parseSaving(r.saving) > 0;
    })
    .map((r) => ({ ...r, savingNum: parseSaving(r.saving) }))
    .sort((a, b) => b.savingNum - a.savingNum);
  const totalSaving = withSaving.reduce((s, r) => s + r.savingNum, 0);
  const actions = withSaving.map((r, i) => {
    let actionLabel = 'Review →';
    let actionClass = 'mini-btn';
    if (r.status === 'Stopped') { actionLabel = 'Terminate →'; actionClass = 'mini-btn ghost'; }
    else if (r.recType === 'RIGHTSIZE') actionLabel = 'Rightsize →';
    else if (r.recType === 'SCALE-UP') { actionLabel = 'Scale up →'; actionClass = 'mini-btn ghost'; }
    return { id: i + 1, title: r.name || r.id, amount: r.saving, desc: `${r.type} ${r.recLabel} · ${r.recommendationRemark}`, actionLabel, actionClass, row: r };
  });
  return { actions, totalSaving };
};

// ── Table Columns ──────────────────────────────────────────
const ec2Columns = [
  { key: 'id', label: 'Instance ID' },
  { key: 'name', label: 'Name' },
  {
    key: 'status', label: 'Status',
    render: (row) => <span className={`status-pill ${row.status === 'Running' ? 'st-running' : 'st-stopped'}`}>{row.status}</span>,
  },
  { key: 'type', label: 'Type' },
  { key: 'region', label: 'Region' },
  { key: 'cpu', label: 'CPU Avg', render: (row) => <UtilBar avgValue={row.cpu} peakValue={row.peakCpu} /> },
  { key: 'p99Cpu', label: 'P99 CPU', className: 'num', render: (row) => row.p99Cpu != null ? <span>{row.p99Cpu}%</span> : <span style={{ color: 'var(--muted)' }}>—</span> },
  { key: 'p95Cpu', label: 'P95 CPU', className: 'num', render: (row) => row.p95Cpu != null ? <span>{row.p95Cpu}%</span> : <span style={{ color: 'var(--muted)' }}>—</span> },
  { key: 'cost', label: 'Cost / Mo', className: 'num', render: (row) => <CostCell row={row} /> },
  { key: 'recommendationRemark', label: 'Recommendation', render: (row) => <RecCell row={row} /> },
  { key: 'saving', label: 'Saving / Mo', className: 'num', render: (row) => <SavingCell row={row} /> },
  { key: 'recommendedCost', label: 'Payable Cost / Mo', className: 'num', render: (row) => <RecommendedCostCell row={row} /> }, 
  { key: 'action', label: 'Action', render: (row) => <button className={row.actionClass} onClick={row.onAction ? () => row.onAction(row) : undefined}>{row.actionLabel}</button> },
];

// ── Main Page ──────────────────────────────────────────────
const EC2Optimization = () => {
  const { selectedAccount } = useAuth();

  const [activeTab, setActiveTab] = useState(0);

  const [isLoadingCombined, setIsLoadingCombined] = useState(false);
  const [isLoadingCustom, setIsLoadingCustom] = useState(false);
  const [isLoadingAWS, setIsLoadingAWS] = useState(false);

  const [combinedRows, setCombinedRows] = useState([]);
  const [customRows, setCustomRows] = useState([]);
  const [awsRows, setAwsRows] = useState([]);

  const [combinedKpi, setCombinedKpi] = useState([]);
  const [customKpi, setCustomKpi] = useState([]);
  const [awsKpi, setAwsKpi] = useState([]);

  const [byRegionData, setByRegionData] = useState(null);
  const [byTypeData, setByTypeData] = useState(null);
  const [trendData, setTrendData] = useState(null);
  const [rightsizeData, setRightsizeData] = useState(null);
  const [insightActions, setInsightActions] = useState([]);
  const [insightTotal, setInsightTotal] = useState(0);

  const { tagList } = useTagList('EC2');
  const [filterList, setFilterList] = useState({ tag: '', values: [] });

  const [aiOpen, setAiOpen] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiData, setAiData] = useState(null);

  const handleDetailsClick = (row) => {
    setAiData({
      instanceId: row.id, instanceName: row.name, instanceType: row.type,
      status: row.status, region: row.region, cpuAvg14dPercent: row.cpu,
      currentMonthlyCost: row.cost, recommendation: row.recType,
      recommendedInstance: row.recLabel, estimatedMonthlySaving: row.saving,
    });
    setAiPrompt(
      'You are a cloud cost advisor. Based on the data provided, give a short and natural insight ' +
      'as if you are speaking directly to the user — not reading a report. ' +
      'Mention the cost situation, what savings are within reach, and what the user should consider doing. ' +
      'Keep it conversational, friendly, and to the point. Do not mention field names or JSON.'
    );
    setAiOpen(true);
  };

  const fetchedTabsRef = useRef(new Set());

  const fetchTabData = (tabIndex, isForceRefresh = false) => {
    if (!selectedAccount) return;
    if (!isForceRefresh && fetchedTabsRef.current.has(tabIndex)) return;
    fetchedTabsRef.current.add(tabIndex);

    if (tabIndex === 0) {
      setIsLoadingCombined(true);
      setCombinedRows([]);
      OptimizationService.getTabularData(selectedAccount, 'COMBINED_EC2', null, isForceRefresh)
        .then((data) => {
          setCombinedRows(mapCombinedRows(data?.rows ?? []));
          setCombinedKpi(buildKpiData(data.totalPriceValue, data?.rows?.length ?? 0, data?.rows ?? []));
        })
        .finally(() => setIsLoadingCombined(false));
    } else if (tabIndex === 1) {
      setIsLoadingCustom(true);
      setCustomRows([]);
      OptimizationService.getTabularData(selectedAccount, 'CUSTOM_EC2', null, isForceRefresh)
        .then((data) => {
          setCustomRows(mapCustomRows(data?.rows ?? []));
          setCustomKpi(buildKpiData(data.totalPriceValue, data?.rows?.length ?? 0, data?.rows ?? []));
        })
        .finally(() => setIsLoadingCustom(false));
    } else {
      setIsLoadingAWS(true);
      setAwsRows([]);
      OptimizationService.getTabularData(selectedAccount, 'EC2', null, isForceRefresh)
        .then((data) => {
          setAwsRows(mapAWSRows(data?.rows ?? []));
          setAwsKpi(buildKpiData(data.totalPriceValue, data?.rows?.length ?? 0, data?.rows ?? []));
        })
        .finally(() => setIsLoadingAWS(false));
    }
  };

  const handleTabChange = (i) => { setActiveTab(i); fetchTabData(i); };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (selectedAccount && selectedAccount !== ADD_NEW_ACCOUNT) { setFilterList({ tag: '', values: [] }); fetchedTabsRef.current = new Set(); fetchTabData(0); } }, [selectedAccount]);

  // filteredCustomRows: client-side tag filter on the full custom dataset — no extra API call
  const filteredCustomRows = useMemo(() => {
    if (!filterList.tag || filterList.values.length === 0) return customRows;
    return customRows.filter((row) => {
      const tags = row.tags ?? [];
      return tags.some((t) => (t.Key ?? t.key) === filterList.tag && filterList.values.includes(t.Value ?? t.value));
    });
  }, [customRows, filterList]);

  useEffect(() => {
    const rows = activeTab === 0 ? combinedRows : activeTab === 1 ? filteredCustomRows : awsRows;
    if (rows.length > 0) {
      setByRegionData(buildGroupedCostDonut(rows, 'region'));
      setByTypeData(buildByTypeChartData(rows));
      setTrendData(buildTrendChartData(rows));
      setRightsizeData(buildRightsizeChartData(rows));
      const { actions, totalSaving } = buildInsightActions(rows);
      setInsightActions(actions);
      setInsightTotal(totalSaving);
    } else {
      setByRegionData(null); setByTypeData(null); setTrendData(null); setRightsizeData(null);
      setInsightActions([]); setInsightTotal(0);
    }
  }, [activeTab, combinedRows, filteredCustomRows, awsRows]);

  const activeKpi = activeTab === 0 ? combinedKpi : activeTab === 1 ? customKpi : awsKpi;
  const activeRows = (activeTab === 0 ? combinedRows : activeTab === 1 ? filteredCustomRows : awsRows)
    .map((row) => ({ ...row, onAction: handleDetailsClick }));

  // Keep AI chatbot context in sync with the data shown on this page
  useEffect(() => {
    const rows = activeTab === 0 ? combinedRows : activeTab === 1 ? filteredCustomRows : awsRows;
    setScopedItem(STORAGE_KEYS.AI_CHATBOT_REQUEST_DATA, {
      page: 'EC2 Instance Optimization',
      kpi: activeKpi,
      ec2Recommendations: rows,
    });
  }, [activeTab, combinedRows, filteredCustomRows, awsRows]);
  const isLoadingActive = activeTab === 0 ? isLoadingCombined : activeTab === 1 ? isLoadingCustom : isLoadingAWS;

  const rightsizeCandidateCount = activeRows.filter((r) => r.cpu !== null && r.cpu < 30 && r.status === 'Running').length;

  const handleExport = () =>
    exportCSV(activeRows, ec2Columns.filter((c) => !['cpu', 'action'].includes(c.key)), buildExportFilename(`ec2_instance_optimization_${['combined', 'cloudsanalytics', 'aws'][activeTab] ?? activeTab}`, selectedAccount));

  const handleFilterChange = (field, value) => {
    if (field === 'tag') { setFilterList({ tag: value ?? '', values: [] }); return; }
    if (field === 'values') setFilterList((prev) => ({ tag: prev.tag, values: typeof value === 'string' ? value.split(',') : (value ?? []) }));
  };

  return (
    <>
      <div className="breadcrumb">
        Optimization <span style={{ color: 'var(--muted-2)' }}>›</span> <strong>EC2 Instance</strong>
      </div>

      <div className="page-head">
        <div>
          <h1>
            EC2 Instance Optimization{' '}
            {isLoadingActive ? <BadgeSkeleton /> : <span className="badge">{activeRows.length} instances</span>}
          </h1>
          <div className="meta">View, rightsize, schedule, and act on recommendations across all EC2 instances</div>
        </div>
        <div className="head-actions">
          <button className="btn ghost" onClick={handleExport} disabled={activeRows.length === 0}>⬇ Export</button>
          <button className="btn ghost" onClick={() => fetchTabData(activeTab, true)}>↻ Refresh</button>
        </div>
      </div>

      <KpiRow kpiData={activeKpi} loading={isLoadingActive} />

      <div className="grid-3" style={{ marginTop: '14px' }}>
        <Card className="first" title="EC2 CPU vs Cost — Running Instances" subtitle="Avg CPU % vs monthly cost · top 20 by cost · running instances only">
          <LineChart data={trendData} loading={isLoadingActive} />
        </Card>
        <Card className="first" title="Rightsizing opportunities" subtitle={`By utilization bucket · ${rightsizeCandidateCount} candidates`}>
          <VerticalBarChart data={rightsizeData} xAxisLabel="CPU avg 14d" loading={isLoadingActive} yAxisSuffix="instances" />
        </Card>
        <TopActionsCard title="Top EC2 actions" actions={insightActions} total={insightTotal} loading={isLoadingActive} entityName="instances" onAction={(item) => item.row && handleDetailsClick(item.row)} />
      </div>

      <Tabs tabs={tabs} defaultActive={0} onTabChange={handleTabChange} />

      {activeTab === 1 && (
        <div className="filter-bar">
          <div className="field">
            <label>Filter Tag</label>
            <select value={filterList.tag} onChange={(e) => handleFilterChange('tag', e.target.value)}>
              <option value="">None</option>
              {tagList.map((tag) => <option key={tag?.Key} value={tag?.Key}>{tag?.Key}</option>)}
            </select>
          </div>
          <div className="field">
            <label>Filter Tag Values</label>
            <MultiSelect
              options={tagList.find((t) => t?.Key === filterList.tag)?.Value ?? []}
              value={filterList.values}
              onChange={(vals) => handleFilterChange('values', vals)}
              disabled={!filterList.tag}
            />
          </div>
          <div className="spacer" />
          <button className="btn ghost" onClick={() => setFilterList({ tag: '', values: [] })}>✕ Clear</button>
          <button className="btn primary" onClick={() => fetchTabData(activeTab, true)}>↻ Refresh</button>
        </div>
      )}

      <Table
        rows={activeRows}
        columns={ec2Columns}
        loading={isLoadingActive}
        skeletonRows={8}
        pageSizes={[10, 25, 50, 100]}
        defaultPageSize={10}
        defaultSortKey="saving"
        defaultSortDir="desc"
        onExport={handleExport}
        emptyText="No EC2 instances found."
        subscriptionGated
      />

      <div className="grid-2" style={{ marginTop: '14px' }}>
        <Card className="first" title="Cost by Instance Type" subtitle="Top 6 instance families this month">
          <HorizontalBarChart data={byTypeData} loading={isLoadingActive} />
        </Card>
        <Card className="first" title="Cost by Region" subtitle="Active EC2 regions">
          <DonutChart data={byRegionData} loading={isLoadingActive} />
        </Card>
      </div>

      <AIModal
        open={aiOpen}
        onClose={() => { setAiOpen(false); setAiPrompt(''); setAiData(null); }}
        prompt={aiPrompt}
        data={aiData}
      />
    </>
  );
};

export default EC2Optimization;
